import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ImageUploadField, PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, TreeSelect, Select, Textarea } from 'components/common';
import { createConsulting, getAdminConsultingById, updateConsulting } from 'apis/adminConsultings';
import { getAdminEcosystemGroups } from 'apis/adminEcosystemGroups';
import { getPublicNavMenusByLanguage } from 'apis/adminNavMenus';
import {
    useActiveLanguagesQuery,
    useAdminCreateMutation,
    useAdminDetailQuery,
    useAdminListQuery,
    useAdminUpdateMutation,
} from 'hooks';
import { buildMenuTree, findMenuByKey } from 'utils/menuTree';
import '../../../styles/admin-shared.scss';

const ConsultingsForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({ navMenuId: '', ecosystemGroupId: '', image: '', sortOrder: 0, isActive: true, translation: [] });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: groupsData = { items: [] } } = useAdminListQuery(
        'ecosystem-groups-options',
        { pageSize: 100, languageCode: 'vi' },
        getAdminEcosystemGroups
    );
    const ecosystemGroups = groupsData.items || [];

    const { data: navMenus = [] } = useQuery({
        queryKey: ['admin', 'consultings', 'nav-menu-options'],
        queryFn: () => getPublicNavMenusByLanguage('vi'),
        select: (response) => {
            const menuTree = buildMenuTree(response?.data || []);
            const investmentMenu = findMenuByKey(menuTree, 'menu.investment');
            const ecosystemMenu = findMenuByKey(menuTree, 'about_menu.ecosystem');
            return [
                ...(investmentMenu?.children || []),
                ...(ecosystemMenu?.children || []),
            ];
        },
        staleTime: 5 * 60 * 1000,
    });
    const { data: detail, error: detailError } = useAdminDetailQuery('consultings', id, getAdminConsultingById);
    const createMutation = useAdminCreateMutation('consultings', createConsulting);
    const updateMutation = useAdminUpdateMutation('consultings', updateConsulting);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({ ...prev, translation: languages.map(l => ({ languageId: l.id, description: '' })) }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail) setFormData({ ...detail, navMenuId: detail.navMenuId || '', ecosystemGroupId: detail.ecosystemGroupId || '' });
    }, [detail]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại');
    }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({ ...prev, translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData, navMenuId: Number(formData.navMenuId), ecosystemGroupId: formData.ecosystemGroupId || null, sortOrder: Number(formData.sortOrder) };
            const res = id
                ? await updateMutation.mutateAsync({ id, data: payload })
                : await createMutation.mutateAsync(payload);
            if (res.success) { toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công'); navigate('/admin/consultings'); }
            else { if (res.messageError) setErrors(res.messageError); else toast.error(res.message || 'Lưu thất bại'); }
        } catch { toast.error('Lưu thất bại'); }
    };

    const ecosystemOptions = [
        { value: '', label: 'Không thuộc hệ sinh thái (Mặc định)' },
        ...ecosystemGroups.map(g => ({ value: g.id, label: g.name || g.code }))
    ];

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Dịch vụ Tư vấn' : 'Thêm Dịch vụ Tư vấn'} />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Cài đặt</p>
                <div className="form-row">
                    <TreeSelect
                        label="Menu Điều hướng *"
                        name="navMenuId"
                        value={formData.navMenuId}
                        onChange={handleChange}
                        items={navMenus}
                        placeholder="Chọn menu..."
                        required
                        error={errors.navMenuId}
                    />
                    <Select
                        label="Hệ sinh thái (Tùy chọn)"
                        name="ecosystemGroupId"
                        value={formData.ecosystemGroupId}
                        onChange={handleChange}
                        options={ecosystemOptions}
                    />
                    <Input label="Thứ tự sắp xếp" name="sortOrder" type="number" value={formData.sortOrder} onChange={handleChange} />
                </div>
                <ImageUploadField
                    label="Hình ảnh tư vấn"
                    value={formData.image}
                    folder="consultings"
                    onChange={(image) => setFormData(prev => ({ ...prev, image }))}
                />
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />
                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">
                                {languages.map((lang, i) => <button key={lang.id} type="button" className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{lang.name}</button>)}
                            </div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => t.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Textarea label="Mô tả" value={t.description || ''} onChange={e => handleTranslationChange(lang.id, 'description', e.target.value)} rows={5} />
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}
                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật' : 'Thêm mới'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/consultings')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default ConsultingsForm;
